import { Link } from 'react-router-dom';
import { Heart, MessageCircle, Camera } from 'lucide-react';

interface Post {
  id: string;
  content: string | null;
  image_url: string | null;
  likes_count: number;
  comments_count: number;
  created_at: string;
  pets: {
    name: string;
    avatar_url: string | null;
  } | null;
}

interface ProfileGridProps {
  posts: Post[];
  loading?: boolean;
}

const ProfileGrid = ({ posts, loading }: ProfileGridProps) => {
  if (loading) {
    return (
      <div className="grid grid-cols-3 gap-1 md:gap-2">
        {[...Array(9)].map((_, i) => (
          <div 
            key={i} 
            className="aspect-square bg-muted rounded-lg animate-pulse"
          />
        ))}
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className="text-center py-16">
        <Camera className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
        <h3 className="text-xl font-semibold text-white mb-2">Nessun post ancora</h3>
        <p className="text-muted-foreground">
          Inizia a condividere i momenti speciali con i tuoi pet!
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-3 gap-1 md:gap-2">
      {posts.map((post) => (
        <Link
          key={post.id}
          to={`/app/post/${post.id}`}
          className="group relative aspect-square overflow-hidden rounded-lg bg-muted"
        >
          {/* Post Image or Placeholder */}
          {post.image_url ? (
            <img
              src={post.image_url}
              alt={post.content || 'Post'}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
              <div className="text-center">
                <Camera className="w-8 h-8 mx-auto mb-2 text-primary" />
                {post.pets && (
                  <p className="text-xs text-white font-medium">
                    {post.pets.name}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Overlay with stats */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-all duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100 pointer-events-none" aria-hidden="true">
            <div className="flex items-center gap-4 text-white">
              <div className="flex items-center gap-1">
                <Heart className="w-5 h-5 fill-current" />
                <span className="font-semibold">{post.likes_count}</span>
              </div>
              <div className="flex items-center gap-1">
                <MessageCircle className="w-5 h-5 fill-current" />
                <span className="font-semibold">{post.comments_count}</span>
              </div>
            </div>
          </div>

          {/* Pet indicator */}
          {post.pets && (
            <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <div className="bg-black/70 rounded-full px-2 py-1 text-xs text-white">
                🐾 {post.pets.name}
              </div>
            </div>
          )}

          {/* Multiple items indicator (if it's a carousel) */}
          <div className="absolute top-2 right-2">
            <div className="w-2 h-2 bg-white rounded-full shadow-lg" />
          </div>
        </Link>
      ))}
    </div>
  );
};

export default ProfileGrid;